import React, { useRef, useState } from 'react'
import { CheckCircle2, MonitorUp, Presentation, RefreshCw, Video } from 'lucide-react'
import { genId } from '../lib/id'
import { lessonApi, recorderApi } from '../api/lessonBuilderApi'
import { useLessonBuilder } from '../store/useLessonBuilder'
import type { PptxCard } from '../types/LessonDoc'
import type { CardDef, CardEditorProps, CardPreviewProps } from './types'
import { InteractiveVideoPlayer, QuestionInsertModal, VideoCheckpointTimeline, VideoQuestionEditor } from './InteractiveVideoQuestions'

function recorderProtocolUrl(opts: { token: string; uploadUrl: string; lessonId: string; cardId: string; title?: string }) {
  const q = new URLSearchParams()
  q.set('token', opts.token)
  q.set('uploadUrl', opts.uploadUrl)
  q.set('lessonId', opts.lessonId)
  q.set('cardId', opts.cardId)
  if (opts.title) q.set('title', opts.title)
  return `vatli365-recorder://record?${q.toString()}`
}

const Editor: React.FC<CardEditorProps> = ({ card, onChange, lessonId }) => {
  const c = card as PptxCard
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string>('')
  const previewRef = useRef<HTMLVideoElement | null>(null)
  const [insertTime, setInsertTime] = useState<number | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const editingCheckpoint = (c.checkpoints || []).find((cp) => cp.id === editingId) || null

  const openRecorder = async () => {
    if (!lessonId) {
      setMsg('Cần lưu nháp bài giảng trước khi mở PP.')
      return
    }
    setBusy(true)
    setMsg('')
    try {
      let lid = lessonId
      try {
        const saved = await lessonApi.save(useLessonBuilder.getState().lesson)
        if (saved?.id) lid = saved.id
      } catch {
        // van thu tao phien voi id hien co
      }
      const session = await recorderApi.createSession(lid, c.id)
      const url = recorderProtocolUrl({
        token: session.token,
        uploadUrl: session.uploadUrl,
        lessonId: lid,
        cardId: c.id,
        title: c.title || 'Trình chiếu PowerPoint',
      })
      window.location.href = url
      setMsg('Đã gửi lệnh mở App. Nếu trình duyệt hỏi quyền mở ứng dụng, hãy chọn Allow/Mở App.')
    } catch (e) {
      setMsg(`Chưa mở được App ghi PowerPoint: ${(e as Error)?.message || e}. Hãy lưu bài giảng trước rồi thử lại.`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-orange-100 bg-orange-50 p-4 text-sm text-orange-800">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-orange-700">
            <Presentation size={20} />
          </span>
          <div className="space-y-2">
            <p className="font-black">Quy trình trình chiếu và ghi bài từ PowerPoint</p>
            <ol className="list-decimal space-y-1 pl-5">
              <li>Bấm <strong>Mở PP</strong> để mở App ghi PowerPoint trên máy tính.</li>
              <li>Trong App, chọn file PowerPoint có sẵn trên máy.</li>
              <li>Bấm <strong>Mở PowerPoint</strong> hoặc <strong>Trình chiếu</strong>; App sẽ mở PowerPoint thật của Windows.</li>
              <li>Giáo viên trình chiếu và giảng bài trực tiếp như bình thường.</li>
              <li>Bấm <strong>Bắt đầu ghi</strong> để ghi màn hình + micro, bấm <strong>Dừng ghi</strong> khi giảng xong.</li>
              <li>Bấm <strong>Lưu & upload</strong>; video sẽ tự gắn vào thẻ này trong bài giảng.</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={openRecorder}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-black text-white hover:bg-orange-700 disabled:opacity-50"
        >
          {busy ? <RefreshCw size={17} className="animate-spin" /> : <MonitorUp size={17} />}
          Mở PP
        </button>
        {c.recordingUrl && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700">
            <CheckCircle2 size={14} /> Đã có video ghi
          </span>
        )}
      </div>

      {msg && <p className="text-xs font-semibold text-slate-500">{msg}</p>}

      {c.recordingUrl && (
        <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-3">
          <label className="mb-1 block text-xs font-black uppercase text-emerald-700">Video đã ghi từ PowerPoint</label>
          <input className="w-full rounded-lg border border-emerald-100 bg-white p-2 text-xs text-slate-600 outline-none" value={c.recordingUrl} readOnly />
          <div className="mt-2 overflow-hidden rounded-xl border border-emerald-100 bg-black">
            <video
              ref={previewRef}
              src={c.recordingUrl}
              controls
              preload="metadata"
              className="aspect-video w-full bg-black"
            />
          </div>
          <VideoCheckpointTimeline
            videoRef={previewRef}
            src={c.recordingUrl}
            checkpoints={c.checkpoints || []}
            onAdd={(timeSec) => {
              setInsertTime(timeSec)
              setEditingId(null)
            }}
            onEdit={(checkpointId) => {
              setEditingId(checkpointId)
              setInsertTime(null)
            }}
          />
          <QuestionInsertModal
            open={insertTime !== null || !!editingCheckpoint}
            timeSec={editingCheckpoint?.timeSec ?? insertTime ?? 0}
            editing={editingCheckpoint}
            questionPool={c.questionPool || []}
            onPoolChange={(next) => onChange({ questionPool: next })}
            onConfirm={(question, timeSec) => {
              if (editingCheckpoint) {
                onChange({
                  checkpoints: (c.checkpoints || []).map((cp) => (
                    cp.id === editingCheckpoint.id
                      ? { ...cp, timeSec, question }
                      : cp
                  )),
                })
              } else {
                onChange({
                  checkpoints: [
                    ...(c.checkpoints || []),
                    { id: genId('vchk'), timeSec, maxAttempts: 2, question },
                  ],
                })
              }
              setInsertTime(null)
              setEditingId(null)
            }}
            onDelete={() => {
              if (editingCheckpoint) {
                onChange({ checkpoints: (c.checkpoints || []).filter((cp) => cp.id !== editingCheckpoint.id) })
              }
              setInsertTime(null)
              setEditingId(null)
            }}
            onClose={() => {
              setInsertTime(null)
              setEditingId(null)
            }}
          />
          <button
            type="button"
            onClick={() => onChange({ recordingUrl: undefined, recordingName: undefined, recordedAt: undefined })}
            className="mt-2 text-xs font-bold text-rose-600 hover:underline"
          >
            Gỡ video khỏi thẻ
          </button>
        </div>
      )}

      <VideoQuestionEditor
        questionPool={c.questionPool || []}
        checkpoints={c.checkpoints || []}
        onPoolChange={(next) => onChange({ questionPool: next })}
        onChange={(next) => onChange({ checkpoints: next })}
        hideCheckpointList
        attachAt={() => previewRef.current?.currentTime || 0}
      />
    </div>
  )
}

const Preview: React.FC<CardPreviewProps> = ({ card, onComplete }) => {
  const c = card as PptxCard

  if (c.recordingUrl) {
    const isAudio = /\.(mp3|wav|m4a)(\?|$)/i.test(c.recordingUrl)
    return (
      <div className="space-y-2">
        <InteractiveVideoPlayer
          source={{ kind: 'video', src: c.recordingUrl, isAudio }}
          title={c.title || 'Bản ghi trình chiếu PowerPoint'}
          checkpoints={c.checkpoints || []}
          onComplete={onComplete}
        />
        <p className="text-xs text-gray-500">
          {c.recordingName || 'Bản ghi trình chiếu PowerPoint'}{c.recordedAt ? ` - ${new Date(c.recordedAt).toLocaleString('vi-VN')}` : ''}
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-dashed border-orange-200 bg-orange-50/50 p-5 text-center">
      <Video className="mx-auto mb-2 text-orange-500" size={28} />
      <p className="font-black text-slate-800">Chưa có bản ghi PowerPoint</p>
      <p className="mt-1 text-sm text-slate-500">
        Trong chế độ soạn bài, bấm Mở PP để mở PowerPoint trên máy, trình chiếu, ghi âm/ghi hình và upload vào thẻ này.
      </p>
    </div>
  )
}

export const pptxCardDef: CardDef = {
  type: 'pptx',
  label: 'Trình chiếu PowerPoint',
  description: 'Mở PowerPoint trên máy, trình chiếu và ghi màn hình/micro bằng App',
  icon: Presentation,
  color: 'bg-orange-50 text-orange-600',
  createDefault: (): PptxCard => ({ id: genId(), type: 'pptx' }),
  Editor,
  Preview,
}
